import React, { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending: "bg-[var(--status-pending)]/20 text-[#9a3815]",
  preparing: "bg-[var(--status-preparing)]/40 text-[#8a6500]",
  ready: "bg-[var(--status-ready)]/30 text-[#3f6953]",
  served: "bg-[var(--status-served)]/30 text-[#5d5167]",
  completed: "bg-[var(--brand-2)]/20 text-[var(--brand-2)]",
  cancelled: "bg-gray-200 text-gray-600 line-through",
};

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (type !== "all") params.type = type;
    return api.get("/orders", { params }).then((r) => setOrders(r.data));
  }, [status, type]);

  useEffect(() => {
    load();
  }, [load]);

  const canPay = ["admin", "manager", "cashier"].includes(user?.role);

  const pay = async (id, method) => {
    try {
      await api.post(`/orders/${id}/pay`, { method });
      toast.success("Payment recorded");
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this order?")) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success("Cancelled");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <>
      <PageHeader
        eyebrow="History"
        title="Orders"
        subtitle="Browse all orders. Filter, inspect, take payments, and cancel from one place."
        actions={
          <div className="flex gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-36" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="preparing">Preparing</SelectItem>
                <SelectItem value="ready">Ready</SelectItem>
                <SelectItem value="served">Served</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-36" data-testid="orders-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="dine-in">Dine-in</SelectItem>
                <SelectItem value="takeaway">Takeaway</SelectItem>
                <SelectItem value="delivery">Delivery</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      <div className="bento overflow-hidden" data-testid="orders-table">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 label-eyebrow border-b border-[#EAE7E0] bg-[var(--bg-secondary)]/50">
          <div className="col-span-3">Order</div>
          <div className="col-span-2">Type / Table</div>
          <div className="col-span-2">Customer</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1">Pay</div>
          <div className="col-span-2 text-right">Total</div>
        </div>
        <div className="divide-y divide-[#EAE7E0]">
          {orders.map((o) => (
            <button
              key={o.id}
              onClick={() => { setActive(o); setOpen(true); }}
              className="w-full grid grid-cols-12 gap-2 px-4 py-3 items-center text-left hover:bg-[var(--bg-secondary)]/40"
              data-testid={`order-row-${o.id}`}
            >
              <div className="col-span-3">
                <div className="font-semibold text-sm">{o.order_number}</div>
                <div className="text-[11px] text-[var(--text-secondary)]">{fmtDate(o.created_at)}</div>
              </div>
              <div className="col-span-2 text-sm">
                <div className="capitalize font-semibold">{o.type}</div>
                {o.table_number && <div className="text-[11px] text-[var(--text-secondary)]">Table {o.table_number}</div>}
              </div>
              <div className="col-span-2 text-sm">
                <div className="font-semibold">{o.customer_name || "—"}</div>
                <div className="text-[11px] text-[var(--text-secondary)]">{o.customer_phone || ""}</div>
              </div>
              <div className="col-span-2">
                <span className={`px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold ${STATUS_BADGE[o.status]}`}>{o.status}</span>
              </div>
              <div className="col-span-1">
                <span className={`text-[10px] uppercase tracking-wider font-bold ${o.payment_status === 'paid' ? 'text-[var(--brand-2)]' : 'text-[var(--brand)]'}`}>
                  {o.payment_status}
                </span>
              </div>
              <div className="col-span-2 text-right font-display font-extrabold">{fmtMoney(o.total)}</div>
            </button>
          ))}
          {orders.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-[var(--text-secondary)]">No orders found.</div>
          )}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="order-detail-dialog">
          <DialogHeader><DialogTitle>{active?.order_number}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-xs text-[var(--text-secondary)]">
                {active.type} {active.table_number ? `· Table ${active.table_number}` : ""} · {fmtDate(active.created_at)}
              </div>
              <div className="bento p-3 divide-y divide-[#EAE7E0]">
                {active.items.map((it, i) => (
                  <div key={i} className="flex justify-between py-2 text-sm">
                    <div>
                      <span className="font-semibold">{it.name}</span>
                      <span className="text-[var(--text-secondary)]"> × {it.quantity}</span>
                    </div>
                    <div className="font-semibold">{fmtMoney(it.line_total)}</div>
                  </div>
                ))}
              </div>
              <div className="text-sm space-y-1">
                <div className="flex justify-between"><span className="text-[var(--text-secondary)]">Subtotal</span><span>{fmtMoney(active.subtotal)}</span></div>
                {active.discount > 0 && <div className="flex justify-between text-[var(--brand-2)]"><span>Discount</span><span>-{fmtMoney(active.discount)}</span></div>}
                <div className="flex justify-between"><span className="text-[var(--text-secondary)]">Tax</span><span>{fmtMoney(active.tax)}</span></div>
                <div className="flex justify-between font-display font-extrabold text-lg pt-2 border-t border-[#EAE7E0]"><span>Total</span><span className="text-[var(--brand)]">{fmtMoney(active.total)}</span></div>
              </div>
              {canPay && active.payment_status !== "paid" && active.status !== "cancelled" && (
                <div>
                  <div className="label-eyebrow mb-2">Collect payment</div>
                  <div className="grid grid-cols-4 gap-2">
                    {["cash", "upi", "card", "wallet"].map((m) => (
                      <Button key={m} variant="outline" onClick={() => pay(active.id, m)} className="capitalize" data-testid={`order-pay-${m}`}>{m}</Button>
                    ))}
                  </div>
                </div>
              )}
              {canPay && active.status !== "cancelled" && active.status !== "completed" && (
                <Button variant="outline" className="w-full border-[var(--brand)] text-[var(--brand)]" onClick={() => cancel(active.id)} data-testid="order-cancel-btn">
                  Cancel order
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
